import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Archive, Trash2, Mail, Clock, MoreVertical, Reply, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DialogCloseButton } from '@/components/ui/dialog-close-button';

interface GmailPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  subject: string;
  body: string;
  fromEmail: string;
  fromName?: string;
  toEmail: string;
  toName?: string;
}

export function GmailPreviewModal({
  isOpen,
  onClose,
  subject,
  body,
  fromEmail,
  fromName,
  toEmail,
  toName,
}: GmailPreviewModalProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const displayTo = toName ? `${toName} <${toEmail}>` : toEmail;
  const formattedDate = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim/50"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl h-[85vh] bg-white rounded-xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-end px-4 py-3 border-b bg-preview-surface">
          <DialogCloseButton onClick={onClose} />
        </div>

        {/* Email Toolbar */}
        <div className="flex items-center gap-2 px-4 py-2 border-t border-preview-rule border-b border-b-gray-100 bg-white">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 cursor-default hover:bg-transparent">
            <Archive className="h-4 w-4 text-content-secondary" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 cursor-default hover:bg-transparent">
            <Trash2 className="h-4 w-4 text-content-secondary" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 cursor-default hover:bg-transparent">
            <Mail className="h-4 w-4 text-content-secondary" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 cursor-default hover:bg-transparent">
            <Clock className="h-4 w-4 text-content-secondary" />
          </Button>
          <div className="h-5 w-px bg-preview-rule mx-1" />
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 cursor-default hover:bg-transparent">
            <MoreVertical className="h-4 w-4 text-content-secondary" />
          </Button>
        </div>

        {/* Email Content */}
        <div className="flex-1 overflow-y-auto border-b border-preview-rule">
          <div className="p-6">
            {/* Subject */}
            <h1 className="text-2xl !font-normal text-content-heading mb-4 ml-[52px]">
              {subject || '(No Subject)'}
            </h1>

            {/* Sender Info */}
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-preview-avatar flex items-center justify-center text-on-accent font-medium text-sm">
                {(fromName?.[0] || fromEmail[0] || 'S').toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-content-heading text-sm">
                      {fromName || fromEmail.split('@')[0]}
                    </span>
                    <span className="text-muted-foreground text-xs ml-2">
                      &lt;{fromEmail}&gt;
                    </span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <span className="text-xs text-muted-foreground mr-1">{formattedDate}</span>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 cursor-default hover:bg-transparent">
                      <Star className="h-4 w-4 text-content-subtle" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 cursor-default hover:bg-transparent">
                      <Reply className="h-4 w-4 text-content-subtle" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 cursor-default hover:bg-transparent">
                      <MoreVertical className="h-4 w-4 text-content-subtle" />
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  to {displayTo}
                </div>
              </div>
            </div>

            {/* Email Body */}
            <div
              className="max-w-none text-preview-body ml-[52px] [&_p]:!mb-3 [&_p:last-child]:!mb-0"
              style={{ fontFamily: 'Arial, sans-serif', fontSize: '0.93rem', lineHeight: '1.6' }}
              dangerouslySetInnerHTML={{ __html: body }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-100">
          <div className="flex gap-2 ml-[52px]">
            <Button variant="outline" size="sm" className="text-content-body border-preview-rule !bg-transparent cursor-default pointer-events-none">
              <Reply className="h-4 w-4 mr-2" />
              Reply
            </Button>
            <Button variant="outline" size="sm" className="text-content-body border-preview-rule !bg-transparent cursor-default pointer-events-none">
              Forward
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

